import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config/apiConfig';
import { defaultSiteContent } from '../config/siteConfig';
import { siteContentSnapshot } from '../config/siteContent.snapshot';

function mergeSiteContent(content, fallback) {
  if (!content) return fallback;
  const sections = Object.fromEntries(Object.entries(fallback.sections).map(([key, fields]) => [
    key, { ...fields, ...(content.sections?.[key] || {}) }
  ]));
  return {
    ...fallback,
    ...content,
    images: { ...fallback.images, ...(content.images || {}) },
    sections,
    methodSteps: Array.isArray(content.methodSteps) ? content.methodSteps : fallback.methodSteps,
    services: Array.isArray(content.services) ? content.services : fallback.services,
    recommendations: Array.isArray(content.recommendations) ? content.recommendations : fallback.recommendations,
    testimonials: Array.isArray(content.testimonials) ? content.testimonials : fallback.testimonials
  };
}

export const bundledSiteContent = mergeSiteContent(siteContentSnapshot, defaultSiteContent);

export function normalizeSiteContent(content) {
  return mergeSiteContent(content, bundledSiteContent);
}

const SiteContentContext = createContext(bundledSiteContent);

export function resolveSiteImage(src) {
  if (!src || /^(https?:|data:|blob:)/.test(src)) return src;
  return `${API_BASE_URL}${src.startsWith('/') ? '' : '/'}${src}`;
}

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(bundledSiteContent);
  useEffect(() => {
    let active = true;
    fetch(`${API_BASE_URL}/api/site-content`)
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((data) => { if (active && data.content) setContent(normalizeSiteContent(data.content)); })
      .catch(() => {
        // GitHub Pages sigue usando el snapshot incluido en el build.
      });
    return () => { active = false; };
  }, []);
  const value = useMemo(() => content, [content]);
  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() { return useContext(SiteContentContext); }
